import { useMemo } from 'react';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import { useAppSelector } from 'store/hooks';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import {
  VisibilityScoreRequest,
  VisibilityScoreResponse,
} from 'store/api/apiTypes';
import { reportApiSlice } from 'store/api/reportApiSlice';

/**
 * useCachedVisibilityScore
 * Returns cached visibility score data (company + products) if previously fetched.
 * Does NOT trigger a network request.
 */
export const useCachedVisibilityScore = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<VisibilityScoreRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectVisibilityScore = useMemo(
    () =>
      queryArgs
        ? reportApiSlice.endpoints.getVisibilityScore.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );

  const selectResult = useAppSelector(selectVisibilityScore as any) as {
    data: VisibilityScoreResponse | undefined;
    status: string;
    error?: any;
    isLoading?: boolean;
  };
  const { data, status, error, isLoading: loading } = selectResult;

  const visibilityScore = data as VisibilityScoreResponse | undefined;
  const companyEntity = visibilityScore?.company?.entity;
  const isLoaded = !loading && !!data?.products && !isGenerating;
  const isLoading = loading || isGenerating;

  return {
    visibilityScore,
    companyEntity,
    companyName: companyEntity?.name,
    companyOverallScore: companyEntity?.overall_score,
    productBlocks: visibilityScore?.products || {},
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
  };
};
